import React, { useEffect, useRef, useState } from 'react';
import { Minus, X, Radio, Maximize2 } from 'lucide-react';

let scriptPromise = null;
const loadJitsi = (domain) => {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return scriptPromise;
};

// Floating, minimizable embedded meeting so the dashboard stays usable during the call.
export const JitsiRoom = ({ room, displayName, title, domain = 'meet.jit.si', onClose }) => {
  const frameRef = useRef(null);
  const apiRef = useRef(null);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    let disposed = false;
    loadJitsi(domain)
      .then(() => {
        if (disposed || !frameRef.current) return;
        const api = new window.JitsiMeetExternalAPI(domain, {
          roomName: room,
          parentNode: frameRef.current,
          userInfo: { displayName: displayName || 'Scholar' },
          configOverwrite: {
            startWithAudioMuted: true,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
          },
          interfaceConfigOverwrite: { MOBILE_APP_PROMO: false, SHOW_JITSI_WATERMARK: false },
        });
        apiRef.current = api;
        api.addListener('readyToClose', () => onClose && onClose());
      })
      .catch(() => {});
    return () => {
      disposed = true;
      try { apiRef.current && apiRef.current.dispose(); } catch (e) { /* noop */ }
    };
  }, [room, domain]);

  return (
    <div
      className="fixed z-[70] bottom-4 right-4 bg-zinc-950 border-2 border-orange-500 shadow-2xl flex flex-col"
      style={{ width: minimized ? 260 : 'min(560px, 92vw)', height: minimized ? 52 : 'min(420px, 70vh)' }}
      data-testid="jitsi-room"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-black border-b border-orange-500/40 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Radio className="w-3.5 h-3.5 text-red-500 animate-pulse flex-shrink-0" />
          <span className="text-[11px] font-black uppercase tracking-wider text-white truncate">{title || 'Live Session'}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setMinimized((m) => !m)} className="text-gray-400 hover:text-white" data-testid="jitsi-minimize" title={minimized ? 'Expand' : 'Minimize'}>
            {minimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-4 h-4" />}
          </button>
          <button onClick={() => onClose && onClose()} className="text-gray-400 hover:text-red-400" data-testid="jitsi-close" title="Leave">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Keep the frame mounted (only visually collapsed) so the call is not destroyed on minimize */}
      <div ref={frameRef} className="flex-1 bg-black" style={{ display: minimized ? 'none' : 'block' }} data-testid="jitsi-frame" />
    </div>
  );
};

export default JitsiRoom;
